import { DataGridProps, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { DataGrid } from '@mui/x-data-grid';
import { FormControlLabel, Switch, TextField, InputAdornment, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import './TableWithButtons.css';
import { SortConfig } from "../../utils/sortUtils";


export type RowStatus = 'INACTIVE' | 'ACTIVE';

export const AppTableWithButtons: FC<
    PropsWithChildren<
        DataGridProps & React.RefAttributes<HTMLDivElement>
        & {
            containerProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
            title?: string;
            rowIdField: string;
            pagination?: boolean;
            totalRecords?: number;
            onPageChange?: (page: number, pageSize: number) => void;
            isLoading?: boolean;
            page?: number;
            showStatusToggle?: boolean;
            rowStatus?: RowStatus;
            onStatusChange?: (status: RowStatus) => void;
            showSearch?: boolean;
            defaultSort?: SortConfig;
            onSortChange?: (sort: SortConfig | null) => void;
            onSearch?: (searchTerm: string) => void;
            headerExtra?: React.ReactNode;
        }
    >
> = ({ children, containerProps, className, title, pagination, page = 0, totalRecords = 0,
    onPageChange, rowIdField, rows, isLoading, showStatusToggle, rowStatus, onStatusChange, defaultSort, onSortChange, showSearch, onSearch, headerExtra, ...props }) => {
        const { className: containerClassName, ...otherContainerProps } = containerProps || {};
        const [tablePage, setTablePage] = useState<number>(page);
        const [pageSize, setPageSize] = useState<number>(10);
        const [localStatusToggle, setLocalStatusToggle] = useState<RowStatus>(rowStatus || 'ACTIVE');
        const [sortModel, setSortModel] = useState<GridSortModel>(
            defaultSort
                ? [{ field: defaultSort.field, sort: defaultSort.direction }]
                : []
        );
        const [lastSortedField, setLastSortedField] = useState<string | null>(
            defaultSort?.field || null
        );
        const [prevSortModel, setPrevSortModel] = useState<GridSortModel>([]);
        const [searchTerm, setSearchTerm] = useState<string>('');
        const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

        useEffect(() => () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        }, []);

        const getSortModelIsDirty = useCallback((sortModel: GridSortModel) => {
            if (sortModel.length !== prevSortModel.length) {
                return true;
            }
            return !!sortModel.find((entry, idx) => entry.field !== prevSortModel[idx].field || entry.sort !== prevSortModel[idx].sort);
        }, [prevSortModel]);

        const handleSortModelChange = useCallback((model: GridSortModel) => {
            console.log('Sort Model Change', JSON.stringify(model, null, 2));
            const isDirty = getSortModelIsDirty(model);
            if (!isDirty) {
                return;
            }
            setPrevSortModel(model);
            if (model.length === 0 && lastSortedField) {
                const newModel: GridSortModel = [{ field: lastSortedField, sort: 'asc' }];
                setSortModel(newModel);
                if (onSortChange) {
                    onSortChange({ field: lastSortedField, direction: 'asc' });
                }
            } else if (model.length > 0 && model[0].sort) {
                setSortModel(model);
                setLastSortedField(model[0].field);
                if (onSortChange) {
                    onSortChange({
                        field: model[0].field,
                        direction: model[0].sort
                    });
                }
            }
        }, [onSortChange, lastSortedField, getSortModelIsDirty]);

        const handlePaginationChange = useCallback((model: GridPaginationModel) => {
            setPageSize(model.pageSize);
            setTablePage(model.page);
            if (onPageChange) {
                onPageChange(model.page, model.pageSize);
            }
        }, [onPageChange]);

        const handleStatusToggleChange = useCallback((newStatus: RowStatus) => {
            setLocalStatusToggle(newStatus);
            if (onStatusChange) {
                onStatusChange(newStatus)
            }
        }, [onStatusChange]);

        return (
            <div
                className={`app-table-with-buttons ${containerClassName || ''}`}
                {...otherContainerProps}
            >
                <div className="app-table-with-buttons-wrapper" >
                    <div className="app-table-with-buttons-header" >
                        {
                            title
                                ? (
                                    <div className="app-table-with-buttons-header-item app-table-with-buttons-title" >
                                        <Typography variant="subtitle1">{title}</Typography>
                                    </div>
                                )
                                : null
                        }
                        {
                            headerExtra
                                ? (
                                    <div className="app-table-with-buttons-header-item app-table-with-buttons-extra" >
                                        {headerExtra}
                                    </div>
                                )
                                : null
                        }
                        {
                            showStatusToggle
                                ? (
                                    <div className="app-table-with-buttons-header-item app-table-with-buttons-toggle" >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={localStatusToggle === 'ACTIVE'}
                                                    onChange={(e) => handleStatusToggleChange(e.target.checked ? 'ACTIVE' : 'INACTIVE')}
                                                    inputProps={{ 'aria-label': 'Toggle form / JSON' }}
                                                />
                                            }
                                            label={localStatusToggle === 'ACTIVE' ? 'Active' : 'Inactive'}
                                        />
                                    </div>
                                )
                                : null
                        }
                        {
                            showSearch
                                ? (
                                    <div className="app-table-with-buttons-header-item app-table-with-buttons-search" >
                                        <TextField
                                            size="small"
                                            placeholder="Search by name"
                                            value={searchTerm}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setSearchTerm(value);
                                                if (debounceTimer.current) {
                                                    clearTimeout(debounceTimer.current);
                                                }
                                                debounceTimer.current = setTimeout(() => {
                                                    if (onSearch) {
                                                        onSearch(value);
                                                    }
                                                }, 400);
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <SearchIcon sx={{ color: 'action.active' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </div>
                                )
                                : null
                        }
                    </div>
                    <div className="app-table-with-buttons-table-wrapper" >
                        <DataGrid
                            className={`app-table-with-buttons-table ${className || ''}`}
                            rows={rows}
                            getRowId={(row) => row[rowIdField]}
                            disableRowSelectionOnClick
                            hideFooterPagination={!pagination}
                            hideFooter={!pagination}
                            loading={isLoading}
                            rowCount={totalRecords}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize,
                                        page: tablePage
                                    },
                                },
                            }}
                            pageSizeOptions={[10]}
                            paginationMode="server"
                            onPaginationModelChange={handlePaginationChange}
                            sortingMode="server"
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            disableColumnMenu
                            rowSelection={false}
                            sx={{
                                'overflowX': 'scroll',
                                '--DataGrid-overlayHeight': '2em',
                                // Gives the grid a baseline height when empty so the overlay fits
                                minHeight: 100,

                                // Optional: Ensures the internal scroll area stretches to the minHeight
                                '& .MuiDataGrid-main': {
                                    minHeight: 100,
                                },
                            }}
                            {...props}
                        />
                    </div>
                    <div className="app-table-with-buttons-button-row" >
                        {children}
                    </div>
                </div>
            </div>
        )
    };
